import { createId } from '@paralleldrive/cuid2';
import { Trash2Icon } from 'lucide-react';
import { type RefObject, useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';

import { type SkuAssemblyComponentDTO, type SkuDTO } from '@invenco/products-interface';

import { Button } from '@/components/core/button';
import { DialogBody, DialogDescription, DialogHeader, DialogTitle } from '@/components/core/dialog';
import { Input } from '@/components/core/input';
import { useDialogContext } from '@/components/dialog-with-context';
import { FormDialogContent } from '@/components/forms/form-dialog';
import type { Result } from '@/shared/helpers/Result';

import { SkuBadge } from '../../components/sku-badge';
import { SkuSearchCombobox } from './sku-search-combobox';

type EditableComponent = {
  id: string;
  componentSku?: SkuDTO;
  quantity: number;
};

type ComponentRowEditorProps = {
  index: number;
  row: EditableComponent;
  excludedSkuIds: string[];
  portalContainerRef: RefObject<HTMLDivElement | null>;
  onChange: (row: EditableComponent) => void;
  onDelete: () => void;
};

function ComponentRowEditor({
  index,
  row,
  excludedSkuIds,
  portalContainerRef,
  onChange,
  onDelete,
}: ComponentRowEditorProps) {
  const hasSelectedSku = !!row.componentSku;

  return (
    <div className="grid grid-cols-[minmax(0,1fr)_88px_auto] items-center gap-2 sm:grid-cols-[minmax(0,1fr)_140px_auto]">
      {hasSelectedSku ? (
        <div className="flex min-w-0 items-center gap-2">
          <SkuBadge name={row.componentSku?.name ?? 'Unknown SKU'} type={row.componentSku?.type} />
          {row.componentSku?.description ? (
            <span className="min-w-0 truncate text-sm">{row.componentSku.description}</span>
          ) : null}
        </div>
      ) : (
        <SkuSearchCombobox
          excludedSkuIds={excludedSkuIds}
          portalContainerRef={portalContainerRef}
          onSelect={(componentSku) => onChange({ ...row, componentSku })}
        />
      )}

      <Input
        className="w-full min-w-0"
        type="number"
        min={1}
        step={1}
        aria-label={`Quantity for component ${index + 1}`}
        value={Number.isFinite(row.quantity) ? row.quantity : ''}
        onChange={(event) =>
          onChange({
            ...row,
            quantity: event.currentTarget.value === '' ? 0 : event.currentTarget.valueAsNumber,
          })
        }
      />

      <Button
        type="button"
        size="icon"
        variant="ghost"
        aria-label={`Delete component ${index + 1}`}
        onClick={onDelete}
      >
        <Trash2Icon />
      </Button>
    </div>
  );
}

type Props = {
  components: Partial<SkuAssemblyComponentDTO>[];
  onSubmit: (
    components: Partial<SkuAssemblyComponentDTO>[],
  ) => Promise<Result> | Promise<void> | void;
};

export function ComponentsManagementDialog({ components, onSubmit }: Props) {
  const { isOpen } = useDialogContext();
  const form = useForm();
  const [rows, setRows] = useState<EditableComponent[]>([]);

  // TODO: shouldn't be necessary once using Base UI for dialogs
  const portalContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isOpen) return;
    setRows(
      components.map((component) => ({
        id: component.id ?? createId(),
        componentSku: component.componentSku as SkuDTO | undefined,
        quantity: component.quantity ?? 1,
      })) ?? [],
    );
  }, [isOpen, components]);

  const hasIncompleteRow = rows.some(
    (row) => !row.componentSku || !row.quantity || row.quantity < 1,
  );
  const canSave = rows.length > 0 && !hasIncompleteRow;
  const canAdd = rows.every((row) => !!row.componentSku);

  return (
    <FormDialogContent
      className="sm:max-w-3xl"
      form={form}
      submitLabel="Save"
      onSubmit={() =>
        onSubmit(
          rows.map((row) => ({
            componentSkuId: row.componentSku?.id,
            quantity: row.quantity,
          })),
        )
      }
      isSubmitDisabled={!canSave}
    >
      <DialogHeader>
        <DialogTitle>Manage components</DialogTitle>
        <DialogDescription>
          Add SKUs and the required quantity per assembly to define the components.
        </DialogDescription>
      </DialogHeader>
      <div ref={portalContainerRef} className="absolute top-0 left-0 h-0 w-0 overflow-visible" />
      <DialogBody>
        <div className="space-y-2">
          <div className="grid grid-cols-[minmax(0,1fr)_88px_auto] items-center gap-2 text-xs font-medium tracking-wide text-muted-foreground uppercase sm:grid-cols-[minmax(0,1fr)_140px_auto]">
            <div>SKU / Description</div>
            <div className="text-right whitespace-nowrap">
              <span className="sm:hidden">Qty</span>
              <span className="hidden sm:inline">Qty per Bundle</span>
            </div>
            <div />
          </div>
          {rows.map((row, index) => (
            <ComponentRowEditor
              key={row.id}
              index={index}
              row={row}
              excludedSkuIds={rows
                .filter((candidate) => candidate.id !== row.id && candidate.componentSku?.id)
                .map((candidate) => candidate.componentSku?.id as string)}
              portalContainerRef={portalContainerRef}
              onChange={(updatedRow) =>
                setRows((currentRows) =>
                  currentRows.map((candidate) =>
                    candidate.id === row.id ? updatedRow : candidate,
                  ),
                )
              }
              onDelete={() =>
                setRows((currentRows) => currentRows.filter((candidate) => candidate.id !== row.id))
              }
            />
          ))}
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={() =>
              setRows((currentRows) => [...currentRows, { id: createId(), quantity: 1 }])
            }
            disabled={!canAdd}
          >
            Add component
          </Button>
        </div>
      </DialogBody>
    </FormDialogContent>
  );
}
